import React from 'react';
import { Box, Typography, Paper, Stack, Divider, Link } from '@mui/material';
import { OpenInNew as OpenIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { downloadAuthedBlob } from '../../hooks/useAuthedBlobUrl';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { AuthedKeystoneImage } from '../Keystone/AuthedKeystoneImage';

/**
 * Render block shown in the discharge summary for attached Keystone imaging
 * studies. Used in three places:
 *   - Edit mode: preview below the chip list so the user sees what the final
 *     summary will include.
 *   - Read-only/finalized view: rendered beneath the sanitized HTML content.
 *   - (Later) PDF renderer on the backend will produce the same layout.
 *
 * Each study shows: date · modality · accession, a horizontal thumbnail strip
 * (thumbnails anchor-link to the Keystone partner viewer URL — single click
 * in the finalized PDF opens full imaging), and a "View full report" PDF
 * link when a finalized report PDF is attached.
 */

export interface AttachedStudyForRender {
  studyId: string;
  accessionNumber: string;
  studyDate?: string | Date | null;
  modalityName?: string | null;
  numberOfImages?: number | null;
  reportAvailable?: boolean;
  viewerUrl?: string | null;
  includeThumbnails?: boolean;
  includeViewerLink?: boolean;
  includeReportLink?: boolean;
  thumbnails?: Array<{ attachmentId: string; position: number; publicUrl: string }>;
  reportPdf?: {
    attachmentId: string;
    fileName: string;
    publicUrl: string;
  } | null;
}

interface Props {
  studies: AttachedStudyForRender[];
  compact?: boolean; // shorter spacing when shown inline in edit mode
}

const AttachedImagingBlock: React.FC<Props> = ({ studies, compact = false }) => {
  // Keystone `studyDate` is an INSTANT — `keystone-poller.service.ts` stores
  // Asteris's `StudyDateTime` (DATE-KINDS §2.3, Imaging/Keystone). Render it in
  // the clinic's zone, which is also what the backend PDF does for the same
  // block (`pdf.service.renderImagingStudiesHtml`, `LLLL d, yyyy` in
  // Clinic.timezone) — so the on-screen preview and the printed page agree.
  // Hook first: it must run on every render, including the empty-list return.
  const formatInstant = useFormatInstant();
  if (!studies || studies.length === 0) return null;

  return (
    <Box sx={{ mt: compact ? 1 : 2 }}>
      {/* Section header with Asteris Keystone attribution. Kept inline on
          one row so it consumes no extra vertical space. The <img> hides
          gracefully if the logo file isn't deployed. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant={compact ? 'subtitle2' : 'h6'} fontWeight={700}>
          Imaging Studies
        </Typography>
        <Box
          component="a"
          href="https://asteris.com"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            textDecoration: 'none',
            color: 'text.secondary',
            fontSize: compact ? '0.65rem' : '0.7rem',
            opacity: 0.75,
          }}
        >
          <span>Imaging via</span>
          <Box
            component="img"
            src="/assets/Asteris-Keystone-Omni-logo.png"
            alt="Asteris Keystone Omni"
            onError={(e: any) => {
              // Hide the image if the asset isn't deployed; the text still
              // carries the attribution.
              e.currentTarget.style.display = 'none';
            }}
            sx={{
              height: compact ? 14 : 18,
              width: 'auto',
              verticalAlign: 'middle',
            }}
          />
          <Box component="span" sx={{ fontWeight: 600 }}>
            Asteris Keystone
          </Box>
        </Box>
      </Box>
      <Stack spacing={compact ? 1 : 1.5}>
        {studies.map(s => {
          const showThumbs = s.includeThumbnails !== false && (s.thumbnails ?? []).length > 0;
          const showViewer = s.includeViewerLink !== false && !!s.viewerUrl;
          const showReport = s.includeReportLink !== false && !!s.reportPdf;
          const dateStr = s.studyDate
            ? formatInstant(s.studyDate, 'MMMM d, yyyy')
            : 'Date unknown';

          return (
            <Paper
              key={s.studyId}
              variant="outlined"
              sx={{ p: compact ? 1.25 : 2, bgcolor: 'background.paper' }}
            >
              {/* Header line */}
              <Typography variant="body1" fontWeight={600}>
                {dateStr}
                {s.modalityName ? ` — ${s.modalityName}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Accession {s.accessionNumber}
                {typeof s.numberOfImages === 'number' ? ` · ${s.numberOfImages} images` : ''}
                {s.reportAvailable ? ' · Finalized report' : ''}
              </Typography>

              {/* Thumbnail strip — each thumb is an anchor to the viewer so a
                  single click from the printed summary opens Keystone Omni. */}
              {showThumbs && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 1.25,
                    flexWrap: 'wrap',
                  }}
                >
                  {s.thumbnails!.map(t => (
                    <Box
                      key={t.attachmentId}
                      component={showViewer ? 'a' : 'div'}
                      {...(showViewer
                        ? { href: s.viewerUrl!, target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                      sx={{
                        display: 'block',
                        width: compact ? 96 : 120,
                        height: compact ? 96 : 120,
                        overflow: 'hidden',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: showViewer ? 'pointer' : 'default',
                        textDecoration: 'none',
                        '&:hover': showViewer ? { borderColor: 'primary.main' } : undefined,
                      }}
                    >
                      <AuthedKeystoneImage
                        publicUrl={t.publicUrl}
                        alt={`Image ${t.position}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              {/* Action links */}
              <Box
                sx={{
                  mt: 1.25,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                {showViewer && (
                  <Link
                    href={s.viewerUrl!}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.9rem',
                      fontWeight: 500,
                    }}
                  >
                    <OpenIcon fontSize="small" />
                    View full images on Keystone
                  </Link>
                )}
                {showReport && (
                  <Link
                    component="button"
                    type="button"
                    onClick={() =>
                      downloadAuthedBlob(s.reportPdf!.publicUrl, s.reportPdf!.fileName)
                    }
                    underline="hover"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      p: 0,
                    }}
                  >
                    <PdfIcon fontSize="small" />
                    View full report (PDF)
                  </Link>
                )}
              </Box>
            </Paper>
          );
        })}
      </Stack>
      <Divider sx={{ mt: compact ? 1.5 : 2 }} />
    </Box>
  );
};

export default AttachedImagingBlock;
